import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import {
  ArrowRight, Building2, Globe, UserCheck, Mail, Phone, Linkedin, ShieldCheck,
  Database, Target, CheckCircle2, Sparkles,
} from "lucide-react";

const CONTACT = {
  email: "info@greek-export.gr",
  phone: "+30 6989164257",
  phone_display: "6989 164 257",
};

function fmt(n) {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(0)}M+`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K+`;
  return `${n}+`;
}

const PackageCard = ({ pk, lang }) => {
  const hl = !!pk.badge;
  return (
    <div data-testid={`package-${pk.id}`}
      className={`relative rounded-2xl p-8 transition-shadow duration-300 ${hl ? "border-2 border-emerald-500 bg-gradient-to-br from-emerald-50 to-white shadow-lg lg:-mt-4" : "border border-slate-200 bg-white hover:shadow-xl"}`}>
      {hl && (
        <div className="absolute -top-3 left-6 px-3 py-1 rounded-full bg-emerald-600 text-white text-xs font-bold uppercase tracking-wider" data-testid={`package-${pk.id}-badge`}>
          {pk.badge}
        </div>
      )}
      <div className={`font-mono text-xs font-bold tracking-wider ${hl ? "text-emerald-700" : "text-slate-500"}`}>{pk.name}</div>
      <div className="mt-6 flex items-baseline gap-2">
        <span className="font-heading text-5xl font-bold text-slate-900" data-testid={`package-${pk.id}-price`}>{pk.price}</span>
        <span className="text-sm font-medium text-slate-500">{pk.vat}</span>
      </div>
      <div className="mt-1 text-sm text-slate-600 min-h-[40px]">{pk.units}</div>
      <ul className="mt-6 space-y-3 text-sm text-slate-700">
        {pk.items.map((it) => (
          <li key={it} className="flex items-start gap-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
            <span>{it}</span>
          </li>
        ))}
      </ul>
      <Link to="/contact" data-testid={`order-${pk.id}-btn`}
        className={`mt-8 inline-flex items-center justify-center gap-2 w-full px-5 py-3 text-white rounded-md font-semibold transition-colors ${hl ? "bg-emerald-600 hover:bg-emerald-700" : "bg-slate-900 hover:bg-slate-800"}`}>
        {lang === "el" ? `Παραγγελία ${pk.name}` : `Order ${pk.name}`} <ArrowRight className="w-4 h-4" />
      </Link>
    </div>
  );
};

export default function Directories() {
  const { t, lang } = useI18n();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/public/directories/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const T = {
    el: {
      pill: "Επαγγελματικοί κατάλογοι",
      h1: "Στοχευμένες επαφές επιχειρήσεων εξωτερικού",
      sub: "Έτοιμες λίστες με προσωπικά email και τηλέφωνα υπευθύνων — όχι γενικές φόρμες επικοινωνίας. Στοχεύστε τους σωστούς ανθρώπους από την πρώτη μέρα.",
      ctaSample: "Δείτε δείγμα εργασίας",
      ctaContact: "Παραγγείλτε κατάλογο",
      coverage: "Κάλυψη δεδομένων",
      companies: "Επιχειρήσεις",
      countries: "Χώρες",
      decisionMakers: "Στελέχη με στοιχεία",
      updated: "Τελευταία ενημέρωση",
      whyTitle: "Γιατί οι κατάλογοί μας είναι διαφορετικοί",
      why: [
        { t: "LinkedIn νόμιμου εκπροσώπου", d: "Σύνδεσμος LinkedIn του νόμιμου εκπροσώπου / ιδιοκτήτη / διευθύνοντος της επιχείρησης — όχι γενικά info@ της εταιρείας. Στοχευμένη επικοινωνία με αποφασίζων πρόσωπο.", icon: UserCheck },
        { t: "Verified σε πραγματικό χρόνο", d: "Κάθε στοιχείο ελέγχεται πριν σας παραδοθεί. Υψηλό deliverability, χαμηλό bounce rate.", icon: ShieldCheck },
        { t: "Στοχευμένα κριτήρια", d: "Κλάδος, προϊόν, μέγεθος εταιρείας, χώρα, πόλη — όλα παραμετροποιήσιμα για την εξαγωγική σας στρατηγική.", icon: Target },
        { t: "GDPR-friendly", d: "Legitimate interest basis για B2B outreach. Παρέχονται opt-out instructions.", icon: Database },
      ],
      packagesTitle: "Πακέτα",
      packagesSub: "Επιλέξτε το πακέτο που ταιριάζει στις ανάγκες σας. Μεγαλύτερες ποσότητες & custom πακέτα κατόπιν συνεννόησης.",
      packages: [
        { id: "basic", name: "BASIC", price: "70€", vat: "+ ΦΠΑ", units: "100 στοχευμένες επιχειρήσεις", items: [
          "Επωνυμία επιχείρησης", "Χώρα και πόλη", "Κατηγορία δραστηριότητας", "Επίσημη ιστοσελίδα",
          "Γενικό εταιρικό email", "Εταιρικό τηλέφωνο", "Σύνδεσμος πηγής", "Παράδοση σε Excel"] },
        { id: "advanced", name: "ADVANCED", price: "150€", vat: "+ ΦΠΑ", badge: "Προτεινόμενο", units: "100 στοχευμένες επιχειρήσεις με έρευνα επαγγελματικών επαφών", items: [
          "Επωνυμία επιχείρησης", "Χώρα και πόλη", "Κατηγορία δραστηριότητας", "Επίσημη ιστοσελίδα",
          "Γενικό εταιρικό email", "Εταιρικό τηλέφωνο", "Όνομα και επώνυμο υπευθύνου", "Επαγγελματική θέση υπευθύνου",
          "LinkedIn υπευθύνου", "LinkedIn εταιρείας", "Σύνδεσμοι πηγών και τεκμηρίωσης", "Παράδοση σε Excel"] },
        { id: "premium", name: "PREMIUM", price: "200€", vat: "+ ΦΠΑ", units: "100 στοχευμένες επιχειρήσεις με πλήρη έλεγχο καταλληλότητας", items: [
          "Επωνυμία επιχείρησης", "Χώρα και πόλη", "Κατηγορία δραστηριότητας", "Επίσημη ιστοσελίδα",
          "Γενικό εταιρικό email", "Εταιρικό τηλέφωνο", "Όνομα και επώνυμο υπευθύνου", "Επαγγελματική θέση υπευθύνου",
          "LinkedIn υπευθύνου", "LinkedIn εταιρείας", "Έλεγχος ιστοσελίδας κάθε επιχείρησης", "Ανάλυση προϊόντων και δραστηριότητας",
          "Αξιολόγηση καταλληλότητας συνεργασίας", "Απόσπασμα τεκμηρίωσης και ακριβές URL", "Εξατομικευμένο σχόλιο για κάθε επιχείρηση", "Παράδοση σε Excel"] },
      ],
      packagesNote: "Παράδοση σε αρχείο Excel. Οι κατάλογοι δημιουργούνται βάσει της αγοράς, της χώρας και του κλάδου που επιλέγει ο πελάτης. Τα επιμέρους στοιχεία παρέχονται σύμφωνα με τη διαθεσιμότητα και τις ισχύουσες απαιτήσεις προστασίας δεδομένων.",
      orderTitle: "Έτοιμοι να παραγγείλετε;",
      orderSub: "Επικοινωνήστε μαζί μας για να παραλάβετε τον κατάλογό σας μέσα σε 24-72 ώρες.",
      callUs: "Καλέστε μας",
      emailUs: "Email",
    },
    en: {
      pill: "Business directories",
      h1: "Targeted contacts of foreign companies",
      sub: "Ready-made lists with personal emails and phones of decision-makers — not generic contact forms. Reach the right people from day one.",
      ctaSample: "See a work sample",
      ctaContact: "Order a directory",
      coverage: "Data coverage",
      companies: "Companies",
      countries: "Countries",
      decisionMakers: "Executives with contacts",
      updated: "Last updated",
      whyTitle: "Why our directories are different",
      why: [
        { t: "LinkedIn of legal representative", d: "Decision-maker's LinkedIn — not generic info@. Direct contact with the person who signs off, not a shared inbox.", icon: UserCheck },
        { t: "Verified in real time", d: "Every email is validated before delivery. High deliverability, low bounce rate.", icon: ShieldCheck },
        { t: "Targeted criteria", d: "Industry, product, company size, country, city — fully parameterized for your export strategy.", icon: Target },
        { t: "GDPR-friendly", d: "Legitimate interest basis for B2B outreach. Opt-out instructions included.", icon: Database },
      ],
      packagesTitle: "Packages",
      packagesSub: "Pick the package that fits your needs. Larger volumes & custom packages on request.",
      packages: [
        { id: "basic", name: "BASIC", price: "€70", vat: "+ VAT", units: "100 targeted companies", items: [
          "Company name", "Country and city", "Business category", "Official website",
          "General company email", "Company phone", "Source link", "Delivered in Excel"] },
        { id: "advanced", name: "ADVANCED", price: "€150", vat: "+ VAT", badge: "Recommended", units: "100 targeted companies with business-contact research", items: [
          "Company name", "Country and city", "Business category", "Official website",
          "General company email", "Company phone", "Decision-maker full name", "Decision-maker job title",
          "Decision-maker LinkedIn", "Company LinkedIn", "Source and evidence links", "Delivered in Excel"] },
        { id: "premium", name: "PREMIUM", price: "€200", vat: "+ VAT", units: "100 targeted companies with full suitability check", items: [
          "Company name", "Country and city", "Business category", "Official website",
          "General company email", "Company phone", "Decision-maker full name", "Decision-maker job title",
          "Decision-maker LinkedIn", "Company LinkedIn", "Website check of every company", "Product and activity analysis",
          "Partnership suitability assessment", "Evidence quote and exact URL", "Personalised comment for every company", "Delivered in Excel"] },
      ],
      packagesNote: "Delivered as an Excel file. Lists are built based on the market, country and industry chosen by the client. Fields are provided subject to availability and applicable data-protection requirements.",
      orderTitle: "Ready to order?",
      orderSub: "Contact us to receive your directory within 24-72 hours.",
      callUs: "Call us",
      emailUs: "Email",
    },
  }[lang === "el" ? "el" : "en"];

  return (
    <div className="min-h-screen bg-white" data-testid="directories-page">
      {/* Header */}
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-white/85 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-md bg-[#0052CC] flex items-center justify-center text-white font-bold">GE</div>
            <span className="font-heading font-bold text-slate-900">{t("app.name")}</span>
          </Link>
          <div className="hidden md:flex items-center gap-6 text-sm font-medium text-slate-700">
            <Link to="/hire-rep" className="hover:text-slate-900">{lang === "el" ? "Πρόσληψη Πωλητή Εξωτερικού" : "Hire Sales Rep"}</Link>
            <Link to="/directories" className="text-[#0052CC] font-semibold">{lang === "el" ? "Επαγγελματικοί Κατάλογοι" : "Business Directories"}</Link>
            <Link to="/contact" className="hover:text-slate-900">{lang === "el" ? "Επικοινωνία" : "Contact"}</Link>
          </div>
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <Link to="/login" className="px-4 py-2 text-sm font-semibold bg-[#0052CC] text-white rounded-md hover:bg-[#0043A8]">
              {t("auth.login")}
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-emerald-700 via-emerald-600 to-cyan-700 text-white">
        <div className="absolute -top-32 -left-32 w-[500px] h-[500px] rounded-full bg-cyan-300/20 blur-[120px]" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-28">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 border border-white/20 text-xs uppercase tracking-[0.25em] font-semibold mb-6">
            <Database className="w-3.5 h-3.5" /> {T.pill}
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] max-w-4xl">
            {T.h1}
          </h1>
          <p className="mt-6 text-lg lg:text-xl text-emerald-50 max-w-3xl leading-relaxed">
            {T.sub}
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-3">
            <Link
              to="/contact"
              data-testid="directories-sample-cta"
              className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-white text-slate-900 rounded-md font-semibold hover:bg-slate-100"
            >
              <Sparkles className="w-4 h-4" /> {T.ctaSample}
            </Link>
            <Link
              to="/contact"
              data-testid="directories-contact-cta"
              className="inline-flex items-center justify-center gap-2 px-6 py-3.5 border border-white/30 text-white rounded-md font-semibold hover:bg-white/10 backdrop-blur-sm"
            >
              {T.ctaContact} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Coverage stats */}
      <section className="bg-white py-16 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <div className="text-xs uppercase tracking-[0.25em] font-semibold text-emerald-700 mb-3">{T.coverage}</div>
            <h2 className="font-heading text-2xl lg:text-3xl font-bold text-slate-900">
              {lang === "el" ? "Η βάση δεδομένων μας σε αριθμούς" : "Our database in numbers"}
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-5xl mx-auto" data-testid="directories-stats">
            <StatCard icon={Building2} value={stats ? fmt(stats.companies) : "…"} label={T.companies} color="#0052CC" />
            <StatCard icon={Globe} value={stats ? `${stats.countries}+` : "…"} label={T.countries} color="#10B981" />
            <StatCard icon={UserCheck} value={stats ? fmt(stats.decision_makers) : "…"} label={T.decisionMakers} color="#F59E0B" />
          </div>
          {stats?.updated && (
            <div className="text-center mt-6 text-xs text-slate-500">
              {T.updated}: {stats.updated}
            </div>
          )}
        </div>
      </section>

      {/* Why different */}
      <section className="bg-[#F8FAFC] py-20 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-2xl mx-auto text-center mb-12">
            <h2 className="font-heading text-3xl lg:text-4xl font-bold text-slate-900 tracking-tight">
              {T.whyTitle}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {T.why.map((w, i) => (
              <div key={i} className="bg-white border border-slate-200 rounded-xl p-6 flex gap-4">
                <div className="w-11 h-11 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0">
                  <w.icon className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-slate-900">{w.t}</h3>
                  <p className="mt-1 text-sm text-slate-600 leading-relaxed">{w.d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Packages */}
      <section className="bg-white py-20 lg:py-24" data-testid="directories-packages">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-2xl mx-auto text-center mb-12">
            <div className="text-xs uppercase tracking-[0.25em] font-semibold text-[#0052CC] mb-3">{T.packagesTitle}</div>
            <h2 className="font-heading text-3xl lg:text-4xl font-bold text-slate-900 tracking-tight">
              {lang === "el" ? "Διαφανής τιμολόγηση" : "Transparent pricing"}
            </h2>
            <p className="mt-4 text-slate-600">{T.packagesSub}</p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 max-w-6xl mx-auto items-start">
            {T.packages.map((pk) => <PackageCard key={pk.id} pk={pk} lang={lang} />)}
          </div>
          <div className="max-w-4xl mx-auto mt-8 text-center text-xs text-slate-500 leading-relaxed" data-testid="packages-note">
            {T.packagesNote}
          </div>
        </div>
      </section>

      {/* Order / Contact banner */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#0A1628] to-[#0052CC] text-white">
        <div className="absolute -top-32 right-0 w-[500px] h-[500px] rounded-full bg-emerald-500/20 blur-[120px]" />
        <div className="relative z-10 max-w-5xl mx-auto px-6 lg:px-12 py-16 lg:py-20 text-center">
          <h2 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight">{T.orderTitle}</h2>
          <p className="mt-4 text-slate-200 max-w-2xl mx-auto">{T.orderSub}</p>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
            <a
              href={`tel:${CONTACT.phone}`}
              data-testid="directories-call-link"
              className="bg-white/10 border border-white/20 rounded-xl p-5 hover:bg-white/20 flex items-center gap-3 text-left"
            >
              <Phone className="w-6 h-6 text-emerald-300" />
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-300">{T.callUs}</div>
                <div className="font-heading text-lg font-bold">{CONTACT.phone_display}</div>
              </div>
            </a>
            <a
              href={`mailto:${CONTACT.email}`}
              data-testid="directories-email-link"
              className="bg-white/10 border border-white/20 rounded-xl p-5 hover:bg-white/20 flex items-center gap-3 text-left"
            >
              <Mail className="w-6 h-6 text-emerald-300" />
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-300">{T.emailUs}</div>
                <div className="font-heading text-base font-bold break-all">{CONTACT.email}</div>
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-10 text-sm">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div>© 2026 {t("app.name")}</div>
          <div className="flex gap-6">
            <Link to="/hire-rep" className="hover:text-white">{lang === "el" ? "Πρόσληψη Πωλητή" : "Hire Rep"}</Link>
            <Link to="/directories" className="hover:text-white">{lang === "el" ? "Κατάλογοι" : "Directories"}</Link>
            <Link to="/contact" className="hover:text-white">{lang === "el" ? "Επικοινωνία" : "Contact"}</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

function StatCard({ icon: Icon, value, label, color }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6 text-center hover:shadow-md transition-shadow">
      <div className="w-12 h-12 mx-auto rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}15`, color }}>
        <Icon className="w-6 h-6" />
      </div>
      <div className="mt-4 font-heading text-4xl font-bold text-slate-900">{value}</div>
      <div className="mt-1 text-sm text-slate-500">{label}</div>
    </div>
  );
}
